import { sendTokenFirebase, getUser } from "~/services/loginService";
import checkoutServices from "~/services/checkoutServices";
import authAction from "~/store/actions/authAction";
import meAction from "~/store/actions/meAction";
import { getAuth } from "firebase/auth";

let showModalSuccessPhone = false;

/**
 * Faz login no Firebase com o token custom
 * @param {string} token - Token custom do Firebase
 * @returns {Promise<{user: Object, accessToken: string, expirationTime: number}>}
 */
const loginWithFirebase = async (token) => {
	try {
		await sendTokenFirebase(token);
		const auth = getAuth();
		const user = auth.currentUser;
		
		if (!user) {
			throw new Error("Falha ao autenticar no Firebase: usuário não encontrado");
		}
		
		const accessToken = await user.getIdToken();
		const expirationTime = Date.now() + 3600000; 

		return {
			user,
			accessToken,
			expirationTime,
		};
	} catch (error) {
		throw new Error(`Erro ao fazer login no Firebase: ${error.message}`);
	}
};

/**
 * Busca os dados do usuário da API
 * @returns {Promise<Object>} Dados do usuário
 */
const fetchUserData = async () => {
	try {
		const meResponse = await checkoutServices.me();
		return meResponse?.data || null;
	} catch (error) {
		throw new Error("Error fetching user data");
	}
};

/**
 * Salva os dados do usuário no localStorage
 * @param {Object} userData - Dados do usuário
 * @param {string} accessToken - Token de acesso
 * @param {number} expirationTime - Tempo de expiração do token
 */
const saveUserDataToLocalStorage = (userData, accessToken, expirationTime) => {
	if (!userData) return;
	
	if (userData.id) {
		localStorage.setItem("@prit-web/userId", userData.id);
	}
	
	localStorage.setItem("@prit-web/meData", JSON.stringify(userData));
	localStorage.setItem("@prit-web/accessToken", accessToken);
	localStorage.setItem("@prit-web/accessTokenExpirationTime", expirationTime.toString());
};

/**
 * Atualiza o Redux store com os dados do usuário
 * @param {Function} dispatch - Dispatch do Redux
 * @param {Object} user - Objeto do usuário do Firebase
 * @param {string} accessToken - Token de acesso
 */
const updateReduxStore = (dispatch, user, accessToken) => {
	if (!dispatch) return;
	
	dispatch(authAction.setUser(user));
	dispatch(meAction.setMe(accessToken));
};

export const redirectAfterAuth = (authResult, history, fallbackRoute = "/", fallbackOptions = {}) => {
	if (!authResult?.success) return;

	const route = authResult?.redirectRoute || fallbackRoute;
	const { needsPersonalInfo, ...redirectOptionsWithoutFlag } = authResult?.redirectOptions || {};
	const options = {...fallbackOptions, ...redirectOptionsWithoutFlag};

	if (history?.push) {
		history.push(route, options);
		return;
	}

}

/**
 * Determina a rota de redirecionamento baseado nos dados do usuário
 * @param {Object} options - Opções de configuração
 * @param {Object} userData - Dados do usuário
 * @returns {string|null} Rota de redirecionamento
 */
const determineRedirectRoute = (options, userData) => {
	const { redirectRoute, getRedirectRoute } = options;
	
	if (getRedirectRoute) {
		return getRedirectRoute(userData);
	}
	
	if (redirectRoute) {
		return redirectRoute;
	}
	
	const businessId = localStorage.getItem("@prit-web/businessId");
	return businessId ? `/business/link/${businessId}` : "/";
};

/**
 * Autentica o usuário via Firebase e realiza o fluxo completo de login
 * @param {string} token - Token custom do Firebase
 * @param {Object} options - Opções de configuração
 * @param {Function} options.dispatch - Dispatch do Redux (opcional)
 * @param {Function} options.onSuccess - Callback chamado após sucesso (opcional)
 * @param {Function} options.onError - Callback chamado em caso de erro (opcional)
 * @param {string} options.redirectRoute - Rota para redirecionar após login (opcional)
 * @param {Object} options.redirectOptions - Opções para redirecionamento (opcional)
 * @param {boolean} options.updateRedux - Se deve atualizar o Redux store (default: false)
 * @param {boolean} options.saveToLocalStorage - Se deve salvar no localStorage (default: true)
 * @param {Function} options.getRedirectRoute - Função customizada para determinar a rota (opcional)
 * @returns {Promise<{success: boolean, userData: Object, user: Object, accessToken: string, redirectRoute: string}>}
 */
export const authenticateFirebase = async (token, options = {}) => {
	const {
		dispatch = null,
		onSuccess = null,
		onError = null,
		redirectRoute = null,
		redirectOptions = {},
		updateRedux = true,
		saveToLocalStorage = true,
		getRedirectRoute = null,
		showOpenModalCustom = null
	} = options;

	try {
		const { user, accessToken, expirationTime } = await loginWithFirebase(token);

		// Atualiza o Redux antes de fazer qualquer request (fetchUserData usa o interceptor)
		// para garantir que o token correto seja usado nas requisições subsequentes.
		if (updateRedux) {
			updateReduxStore(dispatch, user, accessToken);
		}

		const userData = await fetchUserData();

		if (!userData) {
			throw new Error("Não foi possível obter os dados do usuário");
		}

		if (saveToLocalStorage) {
			saveUserDataToLocalStorage(userData, accessToken, expirationTime);
		}

		sessionStorage.setItem("@prit/showModalSuccessPhone", true);
		showModalSuccessPhone = true;

		const needsPersonalInfo = !userData?.name;

		const destinationRoute = determineRedirectRoute(
			{ redirectRoute, getRedirectRoute },
			userData
		);

		const result = {
			success: true,
			userData,
			user,
			accessToken,
			expirationTime,
			redirectRoute: !needsPersonalInfo ? destinationRoute : null,
			redirectOptions: {
				...redirectOptions,
				needsPersonalInfo,
				...(needsPersonalInfo && {
					afterNameUpdateRoute: redirectRoute || destinationRoute,
				}),
			},
		};

		if (onSuccess) {
			onSuccess(result);
		}

		return result;
	} catch (error) {
		const errorMessage = error?.response?.data?.message || error?.message || "Erro ao autenticar usuário";
		
		const errorResult = {
			success: false,
			error: errorMessage,
			originalError: error,
		};

		if (onError) {
			onError(errorResult);
		}

		throw errorResult;
	}
};

/**
 * Versão simplificada para casos de uso básicos
 * @param {string} token - Token custom do Firebase
 * @returns {Promise<Object>} Dados do usuário autenticado
 */
export const quickAuth = async (token) => {
	return authenticateFirebase(token, {
		saveToLocalStorage: true,
		updateRedux: true,
		reloadOnSuccess: true
	});
};

export default {
	authenticateFirebase,
	quickAuth,
	showModalSuccessPhone
};

export { showModalSuccessPhone };